import React, { FC, useMemo, useState } from 'react';
import styled from 'styled-components';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { DataGridPremium, IconButton } from '@clatter/ui';
import { getAxiosApiV2WithAuthorization, useDeepCompareEffect } from '@clatter/platform';
import { config } from '../../../../../../../config';
import { createdAtColumn, displayNameColumn } from '../../../../../../../helpers/dataGrid';
import { Block, mapBlockToStore } from 'apps/ng/src/app/services';

const StyledPagesTemplatesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};

  .header {
    display: flex;
    justify-content: flex-end;
  }
`;

export const PagesTemplatesList: FC<{
  templateData: any;
  isLoading?: boolean;
  onEdit: (pageBlock: any) => void;
  onDelete: (pageBlock: any) => void;
}> = ({ templateData, isLoading: isParentLoading, onEdit, onDelete }) => {
  const [pageTemplates, setPageTemplates] = useState<Block[]>([]);

  const isLoading = isParentLoading;

  const columns = useMemo(
    () => [
      { ...displayNameColumn, field: 'displayName' },
      { ...createdAtColumn, field: 'createdAt' },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 130,
        sortable: false,
        disableExport: true,
        renderCell: ({ id, row }) => (
          <>
            {/*// @ts-ignore*/}
            <IconButton data-id={id} onClick={() => onEdit(row)} tooltip="Edit">
              <EditOutlinedIcon />
            </IconButton>

            {/*// @ts-ignore*/}
            <IconButton data-id={id} onClick={() => onDelete(row)} tooltip="Delete">
              <DeleteOutlinedIcon />
            </IconButton>
          </>
        ),
      },
    ],
    [],
  );

  useDeepCompareEffect(() => {
    const pages: Pick<Block, 'id'>[] = templateData?.Template?.pages || [];

    if (pages.length === 0) {
      setPageTemplates([]);
      return;
    }

    const fetchPageBlocks = async () => {
      try {
        const fetchedPages = await Promise.all(
          pages.map(async (page) => {
            if (!page?.id) {
              console.error('No ID for page');
              return null;
            }

            try {
              const { data: pageBlockData } = await getAxiosApiV2WithAuthorization({
                url: `${config.assetServiceBaseUrl}/blocks/${page.id}`,
                method: 'GET',
              });

              return {
                ...mapBlockToStore(pageBlockData),
                template: page,
              };
            } catch (err) {
              console.error(`Failed to fetch page ${page.id}`, err);
              return null;
            }
          }),
        );

        setPageTemplates(fetchedPages.filter(Boolean) as Block[]);
      } catch (err) {
        console.error('Failed to fetch page blocks', err);
      }
    };

    fetchPageBlocks();
  }, [templateData?.Template?.pages]);

  return (
    <StyledPagesTemplatesList>
      {/*@ts-ignore*/}
      <DataGridPremium
        name="template-page-blocks-list"
        loading={isLoading}
        columns={columns}
        rows={pageTemplates}
        disablePersist
      />
    </StyledPagesTemplatesList>
  );
};
